import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Modal, Form } from 'antd';
// API
import { CREATE_CB_PROMO_CODE } from 'api/cbPromocodes/mutations';
// Types
import {
  CreateCbPromoCode,
  CreateCbPromoCodeVariables,
} from 'api/cbPromocodes/types/CreateCbPromoCode';
import { CbPromoCodeFormValues } from 'helpers/cbPromoCodes/cb-promo-code-form-values.interface';
// Helpers
import { cbPromoCodeInputConverter } from 'helpers/cbPromoCodes/cb-promo-code-input.converter';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CreateCbPromoCodeForm from '../CreateCbPromoCodeForm/CreateCbPromoCodeForm';

export type CreateCbPromoCodeModalProps = {
  show: boolean;
  onCancel: () => void;
};

const CreateCbPromoCodeModal: React.FC<CreateCbPromoCodeModalProps> = ({
  show,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [createCbPromoCode, { loading }] = useMutation<
    CreateCbPromoCode,
    CreateCbPromoCodeVariables
  >(CREATE_CB_PROMO_CODE, {
    refetchQueries: ['GetCbPromoCodes'],
    awaitRefetchQueries: true,
  });

  const handleCreate = async (): Promise<void> => {
    const values = (await form.validateFields()) as CbPromoCodeFormValues;

    try {
      setSubmitting(true);
      const input = cbPromoCodeInputConverter(values);
      const { data } = await createCbPromoCode({ variables: { input } });

      successNotification(
        `Promo code "${data?.createCbPromoCode.code}" created.`
      );
      onCancel();
    } catch (error) {
      const message = (error as Error)?.message;

      errorNotification(message);

      // Duplicate vanity code (active codes only) — also pin it on the field;
      // retiring the existing code frees the string for reuse.
      if (message && /already exists/i.test(message)) {
        form.setFields([{ name: 'code', errors: [message] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create promo code"
      destroyOnClose
      visible={show}
      width={720}
      okText="Create"
      cancelText="Cancel"
      onOk={handleCreate}
      onCancel={onCancel}
      confirmLoading={submitting || loading}
    >
      <CreateCbPromoCodeForm formInstance={form} />
    </Modal>
  );
};

export default CreateCbPromoCodeModal;
